import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Building2, CheckCircle, Loader2 } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const TrialPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    schoolName: '',
    contactName: '',
    email: '',
    role: '',
    pupilCount: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    // Simulate submission
    await new Promise(resolve => setTimeout(resolve, 1500));
    setLoading(false);
    navigate('/thank-you');
  };

  if (submitted) {
    return (
      <div className="min-h-screen">
        <Navbar />
        <section className="pt-32 pb-20 px-4">
          <div className="max-w-xl mx-auto text-center">
            <div className="w-20 h-20 bg-bot-cyan/20 rounded-full flex items-center justify-center mx-auto mb-8">
              <CheckCircle className="w-10 h-10 text-bot-cyan" />
            </div>
            <h1 className="font-display text-4xl text-screen-white mb-4" data-testid="trial-success">
              You’re In!
            </h1>
            <p className="font-body text-lg text-screen-white/80 mb-8">
              We’ve sent your sign-up details to <span className="text-bot-cyan">{formData.email}</span>. 
              Check your inbox for next steps.
            </p>
            <Link
              to="/login"
              className="font-display uppercase tracking-wider bg-zap-yellow text-deep-space px-10 py-4 text-xl rounded-sm hover:translate-y-1 hover:shadow-none shadow-[0_4px_0_0_#B2A000] transition-all inline-block"
            >
              Go to Login
            </Link>
          </div>
        </section>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <Navbar />
      
      <section className="pt-32 pb-20 px-4">
        <div className="max-w-xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-dark-panel/80 border border-mid-panel rounded-full px-4 py-2 mb-6">
              <Building2 className="w-4 h-4 text-bot-cyan" />
              <span className="font-mono text-sm text-bot-cyan">SCHOOL SIGN UP</span>
            </div>
            
            <h1 className="font-display text-4xl md:text-5xl text-screen-white mb-4" data-testid="trial-headline">
              Set Up Your <span className="text-zap-yellow">Free</span> School Account
            </h1>
            <p className="font-body text-screen-white/70">
              Completely free. Full features. No card required.
            </p>
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-6" data-testid="trial-form">
            <div>
              <label className="block font-body font-semibold text-sm text-screen-white mb-2">
                School Name *
              </label>
              <input
                type="text"
                required
                value={formData.schoolName}
                onChange={(e) => setFormData({...formData, schoolName: e.target.value})}
                className="w-full bg-deep-space border-2 border-mid-panel text-screen-white font-body rounded-lg p-4 focus:border-bot-cyan focus:ring-2 focus:ring-bot-cyan/20 outline-none transition-all placeholder:text-screen-white/30"
                placeholder="e.g. Oakwood Primary School"
                data-testid="trial-school-name"
              />
            </div>
            
            <div>
              <label className="block font-body font-semibold text-sm text-screen-white mb-2">
                Your Name *
              </label>
              <input
                type="text"
                required
                value={formData.contactName}
                onChange={(e) => setFormData({...formData, contactName: e.target.value})}
                className="w-full bg-deep-space border-2 border-mid-panel text-screen-white font-body rounded-lg p-4 focus:border-bot-cyan focus:ring-2 focus:ring-bot-cyan/20 outline-none transition-all placeholder:text-screen-white/30"
                placeholder="e.g. Sarah Thompson"
                data-testid="trial-contact-name"
              />
            </div>
            
            <div>
              <label className="block font-body font-semibold text-sm text-screen-white mb-2">
                Email Address *
              </label>
              <input
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                className="w-full bg-deep-space border-2 border-mid-panel text-screen-white font-body rounded-lg p-4 focus:border-bot-cyan focus:ring-2 focus:ring-bot-cyan/20 outline-none transition-all placeholder:text-screen-white/30"
                placeholder="you@school.edu"
                data-testid="trial-email"
              />
            </div>
            
            <div>
              <label className="block font-body font-semibold text-sm text-screen-white mb-2">
                Your Role *
              </label>
              <select
                required
                value={formData.role}
                onChange={(e) => setFormData({...formData, role: e.target.value})}
                className="w-full bg-deep-space border-2 border-mid-panel text-screen-white font-body rounded-lg p-4 focus:border-bot-cyan focus:ring-2 focus:ring-bot-cyan/20 outline-none transition-all"
                data-testid="trial-role"
              >
                <option value="">Select your role</option>
                <option value="teacher">Class Teacher</option>
                <option value="senco">SENCO</option>
                <option value="head">Head Teacher</option>
                <option value="it">IT Coordinator</option>
                <option value="other">Other</option>
              </select>
            </div>
            
            <div>
              <label className="block font-body font-semibold text-sm text-screen-white mb-2">
                Approximate Number of Pupils
              </label>
              <select
                value={formData.pupilCount}
                onChange={(e) => setFormData({...formData, pupilCount: e.target.value})}
                className="w-full bg-deep-space border-2 border-mid-panel text-screen-white font-body rounded-lg p-4 focus:border-bot-cyan focus:ring-2 focus:ring-bot-cyan/20 outline-none transition-all"
                data-testid="trial-pupil-count"
              >
                <option value="">Select range</option>
                <option value="1-30">1-30 (Single class)</option>
                <option value="31-100">31-100</option>
                <option value="101-200">101-200</option>
                <option value="201-400">201-400</option>
                <option value="400+">400+</option>
              </select>
            </div>
            
            <button
              type="submit"
              disabled={loading}
              className="w-full font-display uppercase tracking-wider bg-zap-yellow text-deep-space px-10 py-4 text-xl rounded-sm hover:translate-y-1 hover:shadow-none shadow-[0_4px_0_0_#B2A000] transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3"
              data-testid="trial-submit"
            >
              {loading ? (
                <><Loader2 className="w-6 h-6 animate-spin" /> Processing...</>
              ) : (
                'Create Free Account'
              )}
            </button>
            
            <p className="font-mono text-xs text-screen-white/40 text-center">
              By submitting, you agree to our Terms of Service and Privacy Policy.
            </p>
          </form>
        </div>
      </section>
      
      <Footer />
    </div>
  );
};

export default TrialPage;
